import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { APIProvider, Map as GMap, AdvancedMarker, Pin, useMap } from "@vis.gl/react-google-maps";
import { supabase } from "@/lib/supabase";
import { useTours, useTourWithStops, useReleaseTour, useAssignDriver, decodePolyline, STATUS_TOUR_LABEL } from "@/lib/tours";
import { STATUS_STOP_LABEL } from "@/lib/tours";
import { formatDateIso, getDeliverySettings, listDeliveryDates } from "@/lib/calendar";
import { useOrders } from "@/lib/orders";

const HOF = { lat: 52.0392, lng: 8.1503 };
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY ?? "";
const MAP_ID = "sirges-tagesplan";

const dfmt = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long" });

export default function TagesplanPage() {
  const [date, setDate] = useState("");
  const [running, setRunning] = useState(false);
  const [buildResult, setBuildResult] = useState<string | null>(null);

  useEffect(() => {
    if (!date) {
      getDeliverySettings().then((s) => {
        const list = listDeliveryDates(new Date(), s, 5);
        setDate(formatDateIso(list[0]));
      });
    }
  }, [date]);

  const { data: tours = [] } = useTours(date);
  const { data: orders = [] } = useOrders({
    date: date || undefined,
    status: ["placed", "planned"],
  });

  async function buildRoute() {
    setRunning(true);
    setBuildResult(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const res = await fetch("/api/tour?action=route", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sess.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ date }),
      });
      const data = await res.json();
      setBuildResult(JSON.stringify(data, null, 2));
    } catch (e) {
      setBuildResult(`Fehler: ${(e as Error).message}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <APIProvider apiKey={MAPS_KEY} libraries={["marker"]}>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-hof-900">Tagesplan</h1>
          <div className="flex items-center gap-2">
            <input type="date" className="input max-w-xs" value={date} onChange={(e) => setDate(e.target.value)} />
            <button onClick={buildRoute} disabled={running || !date} className="btn-primary">
              {running ? "Optimiere…" : "🗺 Route bauen"}
            </button>
          </div>
        </div>

        {date && (
          <div className="card text-sm text-hof-700">
            <span className="font-medium">{dfmt.format(new Date(date))}</span> ·
            <span className="ml-2">{orders.length} offene Bestellung{orders.length === 1 ? "" : "en"}</span>
          </div>
        )}

        {buildResult && (
          <div className="card text-sm">
            <div className="font-medium mb-1">Build-Ergebnis:</div>
            <pre className="overflow-x-auto text-xs bg-hof-50 p-2 rounded whitespace-pre-wrap">{buildResult}</pre>
          </div>
        )}

        {tours.length === 0 ? (
          <div className="card text-center text-hof-600">
            Noch keine Tour für diesen Tag. Klick "Route bauen" um eine aus den offenen Bestellungen zu erzeugen.
          </div>
        ) : (
          tours.map((t) => <TourDetail key={t.id} tourId={t.id} />)
        )}
      </div>
    </APIProvider>
  );
}

function TourDetail({ tourId }: { tourId: string }) {
  const { data: tour, isLoading } = useTourWithStops(tourId);
  const release = useReleaseTour();
  const assign = useAssignDriver();

  // Fahrer-Liste laden
  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles")
        .select("id, full_name")
        .eq("role", "fahrer")
        .order("full_name");
      return data ?? [];
    },
  });

  if (isLoading || !tour) return <div className="card">Lade Tour…</div>;

  const polyPoints = tour.planned_polyline ? decodePolyline(tour.planned_polyline) : [];

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-hof-900">Tour {tour.id.slice(0, 8)}</h2>
          <div className="text-sm text-hof-600">
            <span className="pill bg-hof-100 text-hof-800">{STATUS_TOUR_LABEL[tour.status]}</span>
            {tour.planned_distance_m != null && (
              <span className="ml-2">
                {Math.round(tour.planned_distance_m / 100) / 10} km ·
                {Math.round((tour.planned_duration_s ?? 0) / 60)} min
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="input max-w-xs"
            value={tour.driver_id ?? ""}
            onChange={(e) => assign.mutate({ id: tour.id, driverId: e.target.value || null })}
          >
            <option value="">— Fahrer wählen —</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.full_name ?? d.id.slice(0, 8)}</option>)}
          </select>
          {(tour.status === "planned" || tour.status === "draft") && (
            <button
              onClick={() => release.mutate({ id: tour.id })}
              disabled={!tour.driver_id || release.isPending}
              className="btn-primary"
            >
              {release.isPending ? "…" : "✓ Tour freigeben"}
            </button>
          )}
        </div>
      </div>

      {polyPoints.length > 0 && (
        <div className="h-80 rounded-xl overflow-hidden">
          <GMap
            mapId={MAP_ID}
            defaultCenter={HOF}
            defaultZoom={10}
            disableDefaultUI={false}
            gestureHandling="cooperative"
          >
            <RoutePolyline points={polyPoints} />
            <AdvancedMarker position={HOF}>
              <Pin background="#5a8b35" borderColor="#365520" glyphColor="#fff" />
            </AdvancedMarker>
            {tour.stops.map((s) =>
              s.order?.address?.geo_lat && s.order?.address?.geo_lng ? (
                <AdvancedMarker
                  key={s.id}
                  position={{ lat: s.order.address.geo_lat, lng: s.order.address.geo_lng }}
                >
                  <Pin background="#fbbf24" borderColor="#a76a40" glyphColor="#fff">
                    {s.seq}
                  </Pin>
                </AdvancedMarker>
              ) : null
            )}
          </GMap>
        </div>
      )}

      <div className="space-y-2">
        {tour.stops.map((s) => (
          <div key={s.id} className="flex items-start gap-3 p-3 rounded-xl border border-hof-100 hover:bg-hof-50/50">
            <div className="bg-wiese-100 text-wiese-800 rounded-full w-8 h-8 grid place-items-center font-bold shrink-0">
              {s.seq}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{s.order?.customer?.display_name ?? "—"}</div>
              <div className="text-sm text-hof-600">
                {s.order?.address?.street}, {s.order?.address?.zip} {s.order?.address?.city}
              </div>
              {s.eta_at && (
                <div className="text-xs text-hof-500 mt-1">
                  ETA {new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(s.eta_at))}
                </div>
              )}
            </div>
            <div className="text-right text-sm">
              <span className="pill bg-hof-100 text-hof-800">{STATUS_STOP_LABEL[s.status]}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoutePolyline({ points }: { points: Array<{ lat: number; lng: number }> }) {
  const map = useMap();
  useEffect(() => {
    if (!map || points.length === 0 || typeof google === "undefined") return;
    const polyline = new google.maps.Polyline({
      path: points,
      geodesic: true,
      strokeColor: "#5a8b35",
      strokeOpacity: 0.9,
      strokeWeight: 4,
    });
    polyline.setMap(map);
    // Fit bounds um Polyline
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 64);
    return () => { polyline.setMap(null); };
  }, [map, points]);
  return null;
}
